/**
 * env-files.ts — 환경 파일 자동 생성.
 *
 * SPEC: docs/specs/cli-rewrite-completeness.md F7, F8
 * Source: bash setup-harness.sh@911c246~1 L880~890 + L954~996.
 *
 * 3 종 산출:
 *   1. .env.example  (csr-supabase / full Track) — Supabase 토큰 가이드
 *   2. .gitignore .env 라인 추가 (없을 때만)
 *   3. .mcp-allowlist  (모든 dev Track) — D35 opt-in security gate
 *
 * 모두 idempotent — 이미 있으면 skip.
 */

import { appendFileSync, existsSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import type { Track } from "./types.js";

const ENV_EXAMPLE_BODY = `# .env.example — csr-supabase Track
# Copy to .env (gitignored) and fill in values: cp .env.example .env

# ===== Supabase Management API (MCP server용) =====
# Personal Access Token — @supabase/mcp-server가 프로젝트 생성/마이그레이션/Edge Functions 배포에 사용
# 발급: https://supabase.com/dashboard/account/tokens
SUPABASE_ACCESS_TOKEN=

# 프로젝트 참조 ID (예: "abcdefghijklmnop")
# 위치: Supabase Dashboard → Project Settings → General
SUPABASE_PROJECT_REF=

# DB 패스워드 (supabase db push 등 직접 DB 접근용)
# 위치: Supabase Dashboard → Project Settings → Database
SUPABASE_DB_PASSWORD=

# ===== Frontend (public, 클라이언트 노출 OK) =====
# 위치: Supabase Dashboard → Project Settings → API
NEXT_PUBLIC_SUPABASE_URL=
NEXT_PUBLIC_SUPABASE_ANON_KEY=

# ===== Optional — 앱 측 AI 기능용 =====
# OPENAI_API_KEY=
# ANTHROPIC_API_KEY=

# ===== Note =====
# - Vercel/Netlify는 별도 CLI login 사용 (env 불필요): vercel login / netlify login
# - Supabase CLI(supabase login)는 OAuth로 ~/.config/supabase/에 토큰 저장 — env 별개
# - .env는 .gitignore됨 (자동 추가). 절대 commit 금지.
`;

const ENV_EXAMPLE_TRACKS: ReadonlyArray<Track> = ["csr-supabase", "full"];

const GITIGNORE_ENV_PATTERN = /^\.env$|^\.env\s/m;

/**
 * .env.example 생성 (csr-supabase/full Track 한정, idempotent).
 * @returns true if created, false if skipped (already exists or non-applicable track)
 */
export function writeEnvExample(projectDir: string, tracks: ReadonlyArray<Track>): boolean {
  if (!tracks.some((t) => ENV_EXAMPLE_TRACKS.includes(t))) {
    return false;
  }
  const path = join(projectDir, ".env.example");
  if (existsSync(path)) {
    return false;
  }
  writeFileSync(path, ENV_EXAMPLE_BODY);
  return true;
}

/**
 * .gitignore에 `.env` 라인 추가. 이미 있으면 skip.
 * @returns true if appended, false if skipped (no .gitignore or .env already listed)
 */
export function addGitignoreEnv(projectDir: string): boolean {
  const path = join(projectDir, ".gitignore");
  if (!existsSync(path)) {
    return false;
  }
  const content = readFileSync(path, "utf8");
  if (GITIGNORE_ENV_PATTERN.test(content)) {
    return false;
  }
  // append with separator (avoid double newlines)
  const sep = content.endsWith("\n") ? "" : "\n";
  appendFileSync(path, `${sep}\n# Secret env (auto-added by agent-harness install)\n.env\n`);
  return true;
}

const NPX_SKILLS_AGENT_DIRS = [".factory/", ".goose/"];
const GITIGNORE_NPX_SKILLS_HEADER =
  "# npx skills add multi-CLI cache (auto-added by agent-harness)";

/**
 * v0.8.0 — `.gitignore`에 `.factory/`, `.goose/` 패턴 추가 (사용자 보고 #3).
 *
 * `npx skills add`가 multi-CLI universal install 동작 — Codex 사용자 환경에서
 * `.factory/skills/`, `.goose/skills/` 자동 생성. 사용자 git noise 회피용 ignore.
 *
 * idempotent — 이미 있으면 skip.
 * @returns added pattern list (empty if all already present or no .gitignore)
 */
export function addGitignoreNpxSkillsAgents(projectDir: string): string[] {
  const path = join(projectDir, ".gitignore");
  if (!existsSync(path)) {
    return [];
  }
  const content = readFileSync(path, "utf8");
  const missing = NPX_SKILLS_AGENT_DIRS.filter((pattern) => {
    // exact line match (이스케이프 후 줄 단위 비교 — 단순화: 문자열 포함)
    const lineRegex = new RegExp(`^${pattern.replace(/\./g, "\\.").replace(/\//g, "/")}\\s*$`, "m");
    return !lineRegex.test(content);
  });
  if (missing.length === 0) {
    return [];
  }
  const sep = content.endsWith("\n") ? "" : "\n";
  const block = [GITIGNORE_NPX_SKILLS_HEADER, ...missing].join("\n");
  appendFileSync(path, `${sep}\n${block}\n`);
  return [...missing];
}

/**
 * .mcp-allowlist 생성 from .mcp.json mcpServers keys (D35 opt-in security gate).
 * mcp-pre-exec.sh hook이 참조. 파일 부재 시 gate disabled.
 * @returns server name list written, or null if skipped (already exists or .mcp.json missing)
 */
export function writeMcpAllowlist(projectDir: string): string[] | null {
  const allowlistPath = join(projectDir, ".mcp-allowlist");
  if (existsSync(allowlistPath)) {
    return null;
  }
  const mcpPath = join(projectDir, ".mcp.json");
  if (!existsSync(mcpPath)) {
    return null;
  }
  let names: string[] = [];
  try {
    const parsed = JSON.parse(readFileSync(mcpPath, "utf8")) as {
      mcpServers?: Record<string, unknown>;
    };
    names = Object.keys(parsed.mcpServers ?? {}).sort();
  } catch {
    return null;
  }
  if (names.length === 0) {
    return [];
  }
  const body = [
    "# MCP Server Allowlist — auto-generated by agent-harness install",
    "# Referenced by mcp-pre-exec.sh hook. Remove or '#' comment any server you want to block.",
    "# Deleting this file disables the gate (allows all MCP calls).",
    "",
    ...names,
    "",
  ].join("\n");
  writeFileSync(allowlistPath, body);
  return names;
}
